/**
 * Mutfak ekranının yeni fiş zilleri (kullanıcı, 3 Kas 2026: eski zil %100
 * seste bile mutfakta duyulmuyordu; tonu Ayarlar > Mutfak ekranları'ndan
 * patron seçiyor, tenants.kitchen_bell).
 *
 * Eski zil saf sinüs ve tam gücün dörtte biriydi. Bunlar tam güçte, kulağın
 * en hassas olduğu 2-3 kHz çevresinde harmonikli dalgalar; sonda bir
 * sıkıştırıcı tepeleri bastırıyor ki ses yükselirken cızırtıya dönmesin.
 */

export type KitchenBellKey = 'zil' | 'tezgah' | 'alarm' | 'bip' | 'yumusak'

export interface KitchenBell {
  key: KitchenBellKey
  name: string
  hint: string
}

export const KITCHEN_BELLS: KitchenBell[] = [
  { key: 'zil', name: 'Klasik zil', hint: 'iki notalı, iki kez çalar' },
  { key: 'tezgah', name: 'Tezgah zili', hint: 'resepsiyon zili gibi, uzun çınlar' },
  { key: 'alarm', name: 'Alarm', hint: 'en keskini, gürültülü mutfak için' },
  { key: 'bip', name: 'Bip bip', hint: 'kısa, art arda üç bip' },
  { key: 'yumusak', name: 'Yumuşak', hint: 'eski zil, sessiz ortam için' },
]

export const DEFAULT_KITCHEN_BELL: KitchenBellKey = 'zil'

export const isKitchenBell = (v: unknown): v is KitchenBellKey =>
  KITCHEN_BELLS.some((b) => b.key === v)

interface Note {
  f: number
  at: number
  len: number
  wave: OscillatorType
  peak: number
}

const pattern = (key: KitchenBellKey): Note[] => {
  switch (key) {
    case 'tezgah':
      // Vurma zil: temel ton ve uyumsuz üst tonları birlikte, uzun sönüm.
      return [0, 0.9].flatMap((at) => [
        { f: 2093, at, len: 1.1, wave: 'sine' as const, peak: 0.9 },
        { f: 5230, at, len: 0.6, wave: 'sine' as const, peak: 0.35 },
        { f: 2637, at, len: 0.9, wave: 'triangle' as const, peak: 0.5 },
      ])
    case 'alarm':
      return [0, 0.3, 0.6, 0.9, 1.2, 1.5].map((at, i) => ({
        f: i % 2 ? 2000 : 2600,
        at,
        len: 0.24,
        wave: 'square' as const,
        peak: 0.55,
      }))
    case 'bip':
      return [0, 0.22, 0.44, 1.0, 1.22, 1.44].map((at) => ({
        f: 2800,
        at,
        len: 0.14,
        wave: 'square' as const,
        peak: 0.5,
      }))
    case 'yumusak':
      return [0, 0.7].flatMap((round) => [
        { f: 880, at: round, len: 0.3, wave: 'sine' as const, peak: 0.25 },
        { f: 1320, at: round + 0.16, len: 0.3, wave: 'sine' as const, peak: 0.25 },
      ])
    case 'zil':
    default:
      return [0, 0.75].flatMap((round) => [
        { f: 1760, at: round, len: 0.45, wave: 'triangle' as const, peak: 1 },
        { f: 2637, at: round + 0.18, len: 0.55, wave: 'triangle' as const, peak: 1 },
        // Aynı notalar sawtooth ile alttan: tını sertleşir, uzaktan seçilir.
        { f: 1760, at: round, len: 0.3, wave: 'sawtooth' as const, peak: 0.25 },
        { f: 2637, at: round + 0.18, len: 0.35, wave: 'sawtooth' as const, peak: 0.25 },
      ])
  }
}

/** Ses kapalıysa ya da tarayıcı izin vermezse sessiz geçer. */
export function playKitchenBell(key: KitchenBellKey = DEFAULT_KITCHEN_BELL) {
  try {
    const Ctx =
      window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const ctx = new Ctx()
    const comp = ctx.createDynamicsCompressor()
    comp.threshold.value = -12
    comp.knee.value = 6
    comp.ratio.value = 8
    comp.attack.value = 0.002
    comp.release.value = 0.15
    // Sıkıştırıcının kıstığını geri ver; sınır 1 (tam güç).
    const master = ctx.createGain()
    master.gain.value = key === 'yumusak' ? 1 : 2.6
    comp.connect(master).connect(ctx.destination)

    const notes = pattern(key)
    let end = 0
    for (const { f, at, len, wave, peak } of notes) {
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.type = wave
      o.frequency.value = f
      const t = ctx.currentTime + at
      g.gain.setValueAtTime(0.0001, t)
      g.gain.exponentialRampToValueAtTime(peak, t + 0.01)
      g.gain.exponentialRampToValueAtTime(0.0001, t + len)
      o.connect(g).connect(comp)
      o.start(t)
      o.stop(t + len + 0.02)
      end = Math.max(end, at + len)
    }
    window.setTimeout(() => void ctx.close(), (end + 0.5) * 1000)
  } catch {
    /* ses yok */
  }
}
